import { z } from 'zod';

import { entityIdentifierSchema } from './chat-entity';

export const activeEntityModeSchema = z.enum(['view', 'edit']);

export type ActiveEntityMode = z.infer<typeof activeEntityModeSchema>;

export const activeEntitySchema = z.object({
    entity: entityIdentifierSchema,
    mode: activeEntityModeSchema,
});

export type ActiveEntity = z.infer<typeof activeEntitySchema>;

export const frontendContextSchema = z.object({
    activeEntities: z.array(activeEntitySchema).optional(),
    url: z.string().optional(),
    journey: z
        .object({
            nextStep: z
                .object({
                    code: z.string(),
                    scope: z.enum(['ROLES', 'LEVELS']).nullable(),
                    guideStep: z.string().nullable(),
                })
                .nullable(),
            skipped: z.array(z.string()),
        })
        .optional(),
});

export type FrontendContext = z.infer<typeof frontendContextSchema>;

export const chatMessageTypeSchema = z.enum(['text', 'regenerate', 'create-jsa-entities']);

export type ChatMessageType = z.infer<typeof chatMessageTypeSchema>;

export const regenerateScopeSchema = z.enum(['all', 'description', 'skills']);

export type RegenerateScope = z.infer<typeof regenerateScopeSchema>;

export const profileRegenerateMessageContentSchema = z.object({
    id: z.string(),
    includeComments: z.boolean().optional(),
    includeReplies: z.boolean().optional(),
    language: z.string().optional(),
    name: z.string(),
    prompt: z.string(),
    scope: regenerateScopeSchema.optional(),
    type: z.enum(['level', 'role', 'position']),
    version: z.union([z.string(), z.number()]),
});

export type ProfileRegenerateMessageContent = z.infer<typeof profileRegenerateMessageContentSchema>;

const alignmentRegenerateFieldsSchema = z.object({
    includeComments: z.boolean(),
    includeReplies: z.boolean(),
    name: z.string(),
    prompt: z.string(),
    /** The alignment's revision the request was made against. */
    version: z.number(),
});

/**
 * Rewrites an alignment's whole position copy (`id` is the alignment), or one task of it (`id` is
 * `<alignment>/<task>`), from the prompt and optionally its open comments. `alignment_section` is no longer
 * sent but still reads from older chat history.
 */
export const alignmentRegenerateMessageContentSchema = z.union([
    alignmentRegenerateFieldsSchema.extend({
        id: z.string().regex(/^[^/]+$/u),
        type: z.literal('alignment_copy'),
    }),
    alignmentRegenerateFieldsSchema.extend({
        id: z.string().regex(/^[^/]+\/[^/]+$/u),
        type: z.enum(['alignment_section', 'alignment_task']),
    }),
]);

export type AlignmentRegenerateMessageContent = z.infer<typeof alignmentRegenerateMessageContentSchema>;

export const regenerateMessageContentSchema = z.union([
    profileRegenerateMessageContentSchema,
    alignmentRegenerateMessageContentSchema,
]);

export type RegenerateMessageContent = z.infer<typeof regenerateMessageContentSchema>;

export const isAlignmentRegenerateContent = (
    content: RegenerateMessageContent,
): content is AlignmentRegenerateMessageContent =>
    content.type === 'alignment_copy' || content.type === 'alignment_section' || content.type === 'alignment_task';

export const chatAttachmentRefSchema = z.object({
    attachmentId: z.string(),
    /** Set to the deletion instant once the uploader removes the attachment; absent or null means live. */
    deletedAt: z.string().nullish(),
    /** Blank on messages persisted before file names were stored with the message. */
    fileName: z.string(),
    /** The file's media type, when the client knows it; AG-UI carries it on the document part's source. */
    mimeType: z.string().nullish(),
});

export type ChatAttachmentRef = z.infer<typeof chatAttachmentRefSchema>;

export const newMessageRequestSchema = z.object({
    attachments: z.array(chatAttachmentRefSchema).optional(),
    context: frontendContextSchema.optional(),
    message: z.string(),
    messageType: z.literal('text').optional(),
    type: z.literal('new'),
});

export type NewMessageRequest = z.infer<typeof newMessageRequestSchema>;

export const regenerateMessageRequestSchema = z.object({
    content: regenerateMessageContentSchema,
    context: frontendContextSchema.optional(),
    type: z.literal('regenerate'),
});

export type RegenerateMessageRequest = z.infer<typeof regenerateMessageRequestSchema>;

export const createJsaEntitiesMessageContentSchema = z.object({
    attachments: z.array(chatAttachmentRefSchema).optional(),
    prompt: z.string(),
});

export type CreateJsaEntitiesMessageContent = z.infer<typeof createJsaEntitiesMessageContentSchema>;

export const createJsaEntitiesMessageRequestSchema = z.object({
    content: createJsaEntitiesMessageContentSchema,
    context: frontendContextSchema.optional(),
    type: z.literal('create-jsa-entities'),
});

export type CreateJsaEntitiesMessageRequest = z.infer<typeof createJsaEntitiesMessageRequestSchema>;

export const resumeRequestSchema = z.object({
    resume: z.literal(true),
    type: z.literal('resume'),
});

export type ResumeRequest = z.infer<typeof resumeRequestSchema>;

/** Re-runs the thread's last user message after its reply failed, without storing the message again. */
export const retryRequestSchema = z.object({
    context: frontendContextSchema.optional(),
    type: z.literal('retry'),
});

export type RetryRequest = z.infer<typeof retryRequestSchema>;

export const userMessageSchema = z.union([
    newMessageRequestSchema,
    regenerateMessageRequestSchema,
    createJsaEntitiesMessageRequestSchema,
    resumeRequestSchema,
    retryRequestSchema,
]);

export type UserMessage = z.infer<typeof userMessageSchema>;
